// @ts-check
/**
 * Oficiálne počítanie úderov (strojopisná metodika).
 *
 * Zdroj: manuál ATF Online, kap. 13 „Počítanie úderov" (jednotliveznaky.htm,
 * uhozyzaradkovani.htm, uzhozyskupinyvelkychpismen.htm, uhozyskupinycislic.htm).
 *
 * ZÁSADNÝ ROZDIEL od bežného „počítadla stlačení": jeden úder NIE JE jedno
 * stlačenie klávesy. Pre slovenskú klávesnicu platí:
 *   - 1 úder: znaky bez Shiftu, medzera, nový riadok
 *   - 2 údery: malé ó, ŕ, ĺ a všetko písané so Shiftom (veľké písmená bez
 *     diakritiky, číslice, niektoré interpunkčné a diakritické znamienka)
 *   - 3 údery: malé ď (mäkčeň = 2 + d = 1) a veľké písmená s dĺžňom
 *   - 4 údery: veľké písmená s mäkčeňom (napr. Č)
 *   - ť a ň sa na slovenskej klávesnici píšu priamo = 1 úder (na rozdiel od českej)
 *
 * Ďalšie pravidlá:
 *   - Skupina veľkých písmen sa píše CapsLockom: každý znak = 1 úder
 *     a +1 za zapnutie a +1 za vypnutie (tie dve stlačenia počítame prirodzene).
 *   - Skupina číslic: počet číslic + 1 úder za stlačenie Shiftu
 *     (napr. 2006 = 5 úderov), teda Shift sa v rámci skupiny neuplatňuje viackrát.
 *   - Ku každému riadku okrem posledného sa pripočíta 1 úder (medzera alebo Enter).
 */

/** Znaky, ktoré sa na slovenskej klávesnici píšu skladaním (mäkčeň/dĺžeň navyše). */
export const COMPOSED_LOWERCASE_2 = new Set(['ó', 'ŕ', 'ĺ']);
export const COMPOSED_LOWERCASE_3 = new Set(['ď']);
export const UPPER_WITH_ACUTE = new Set(['Á', 'É', 'Í', 'Ó', 'Ú', 'Ý', 'Ŕ', 'Ĺ']);
export const UPPER_WITH_CARON = new Set(['Č', 'Š', 'Ž', 'Ť', 'Ď', 'Ň', 'Ľ', 'Ř', 'Ě']);
export const DIRECT_ON_SLOVAK = new Set(['ť', 'ň']);

/**
 * @typedef {Object} KeyContext
 * @property {boolean} [shift]     bol pri znaku podržaný Shift
 * @property {boolean} [capsLock]  bol zapnutý CapsLock
 * @property {boolean} [digitRun]  ide o ďalšiu číslicu v súvislej skupine číslic
 */

/**
 * Počet úderov za jeden znak podľa oficiálnych pravidiel.
 * @param {string} char
 * @param {KeyContext} [context]
 * @returns {number}
 */
export function strokeCost(char, context = {}) {
  if (char === '') return 0;
  if (char === '\n') return 1; // nový riadok (Enter)
  if (char === ' ') return 1;

  // Skupina číslic: Shift sa počíta raz za skupinu.
  if (char >= '0' && char <= '9') {
    return context.digitRun ? 1 : 2;
  }

  if (COMPOSED_LOWERCASE_3.has(char)) return 3;
  if (COMPOSED_LOWERCASE_2.has(char)) return 2;
  if (UPPER_WITH_CARON.has(char)) return 4;
  if (UPPER_WITH_ACUTE.has(char)) return 3;

  // Veľké písmeno písané CapsLockom = 1 úder; so Shiftom = 2 údery.
  if (char !== char.toLowerCase() && char === char.toUpperCase()) {
    if (context.capsLock) return 1;
    return 2;
  }

  // Ostatné znaky so Shiftom (interpunkcia, zátvorky, §, %...).
  if (context.shift) return 2;

  return 1;
}

/** @param {string} ch */
function isUpperLetter(ch) {
  return ch !== ch.toLowerCase() && ch === ch.toUpperCase() && /\p{L}/u.test(ch);
}

/**
 * Spočíta hrubé údery pre celý text (bez chýb, ako cieľový text).
 * Používa sa na overenie obsahu a na zobrazenie rozsahu cvičenia.
 *
 * Skupiny sa vyhodnocujú ako celok, pretože oficiálne pravidlá sú skupinové:
 *  - skupina číslic = počet číslic + 1 (Shift raz za skupinu),
 *  - skupina veľkých písmen = 1 na znak + 2 za CapsLock (zapnutie a vypnutie),
 *    samostatné veľké písmeno = 2 (Shift),
 *  - v skupine sa za dĺžeň pripočíta 1 úder a za mäkčeň 2 údery.
 *
 * @param {string} text
 * @returns {number}
 */
export function officialStrokesForText(text) {
  let total = 0;
  const chars = [...text];
  let index = 0;

  while (index < chars.length) {
    const char = chars[index];

    if (char === '\n' || char === ' ') {
      total += 1;
      index += 1;
      continue;
    }

    if (char >= '0' && char <= '9') {
      let digits = 0;
      while (index < chars.length && chars[index] >= '0' && chars[index] <= '9') {
        digits += 1;
        index += 1;
      }
      total += digits + 1; // Shift sa v skupine počíta raz
      continue;
    }

    if (isUpperLetter(char)) {
      let count = 0;
      let diacritics = 0;
      while (index < chars.length && isUpperLetter(chars[index])) {
        const current = chars[index];
        if (UPPER_WITH_CARON.has(current)) diacritics += 2;
        else if (UPPER_WITH_ACUTE.has(current)) diacritics += 1;
        count += 1;
        index += 1;
      }
      total += (count === 1 ? 2 : count + 2) + diacritics;
      continue;
    }

    total += strokeCost(char, { shift: false, capsLock: false, digitRun: false });
    index += 1;
  }

  return total;
}

/**
 * Pripočíta údery za riadkovanie: ku každému riadku okrem posledného +1.
 * (V texte sú riadky oddelené '\n', takže sa to už počíta ako Enter; funkcia
 * slúži na prípad, keď text riadky nemá a zalamuje sa vizuálne.)
 * @param {number} lineCount
 * @returns {number}
 */
export function lineBreakStrokes(lineCount) {
  return Math.max(0, lineCount - 1);
}
